import { supabase } from './supabase';

export interface VerifiedFactor {
  id: string;
  friendlyName: string | null;
  createdAt: string;
}

export interface Enrollment {
  factorId: string;
  /** The QR code, as an image address to put in an <img>. */
  qrCode: string;
  /** The same key as text, for typing into the authenticator app by hand. */
  secret: string;
}

function messageOf(err: unknown, fallback: string): string {
  const m = (err as { message?: string } | null)?.message;
  return m && m.length < 200 ? m : fallback;
}

/** The authenticator apps this person has set up and confirmed. */
export async function listVerifiedFactors(): Promise<VerifiedFactor[]> {
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error) throw new Error(messageOf(error, "We couldn't load your two-factor settings."));
  return (data?.totp ?? [])
    .filter((f) => f.status === 'verified')
    .map((f) => ({ id: f.id, friendlyName: f.friendly_name ?? null, createdAt: f.created_at }));
}

/**
 * Starts setting up an authenticator app. A setup that was begun and never
 * finished is removed first, so an abandoned attempt never blocks the next one.
 */
export async function startEnrollment(): Promise<Enrollment> {
  const { data: existing } = await supabase.auth.mfa.listFactors();
  for (const f of existing?.all ?? []) {
    if (f.factor_type === 'totp' && f.status === 'unverified') {
      await supabase.auth.mfa.unenroll({ factorId: f.id });
    }
  }
  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: 'totp',
    friendlyName: `Authenticator ${new Date().toISOString().slice(0, 10)} ${Math.random().toString(36).slice(2, 6)}`,
  });
  if (error || !data) throw new Error(messageOf(error, "We couldn't start the setup. Try again."));
  return { factorId: data.id, qrCode: data.totp.qr_code, secret: data.totp.secret };
}

/** Confirms a code against a factor: finishes the setup, or signs in with it. */
export async function verifyCode(factorId: string, code: string): Promise<void> {
  const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({ factorId });
  if (challengeError || !challenge) throw new Error(messageOf(challengeError, "We couldn't check the code. Try again."));
  const { error } = await supabase.auth.mfa.verify({ factorId, challengeId: challenge.id, code: code.replace(/\s/g, '') });
  if (error) throw new Error('That code did not work. Check the app and try again.');
}

export async function removeFactor(factorId: string): Promise<void> {
  const { error } = await supabase.auth.mfa.unenroll({ factorId });
  if (error) throw new Error(messageOf(error, "We couldn't turn off two-factor authentication."));
}

/**
 * True when this person has set up 2FA but signed in with the password only
 * (or Google only): they must still enter a code before using the app.
 */
export async function needsSecondStep(): Promise<boolean> {
  const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (error || !data) return false;
  return data.nextLevel === 'aal2' && data.currentLevel !== 'aal2';
}

/** Only digits count; spaces are fine ("123 456"). */
export function isValidCode(input: string): boolean {
  return /^\d{6}$/.test(input.replace(/\s/g, ''));
}
